// Status vocabulary shared by the task list, task page and runner:
// colour + icon + word, never colour alone.
import type { RoundSummary } from "@/api/types/RoundSummary";
import { useTranslation } from "react-i18next";
import type { Priority } from "@/api/types/Priority";
import type { ResultStatus } from "@/api/types/ResultStatus";
import type { RoundCounts } from "@/api/types/RoundCounts";
import type { StatusCategory } from "@/api/types/StatusCategory";
import type { StatusColor } from "@/api/types/StatusColor";
import type { TaskStatusRef } from "@/api/types/TaskStatusRef";
import type { TestStage } from "@/api/types/TestStage";
import type { UserRef } from "@/api/types/UserRef";
import { Icon, type IconName } from "./Icon";
import { Avatar, BotTag, Chip, cx } from "./ui";
import { columnTone } from "@/lib/statusColors";

type Tone = "pass" | "fail" | "blocked" | "skip" | "accent" | "neutral" | "outline";

const RESULT: Record<ResultStatus, { tone: Tone; icon: IconName }> = {
  pass: { tone: "pass", icon: "check" },
  fail: { tone: "fail", icon: "x" },
  blocked: { tone: "blocked", icon: "blocked" },
  skipped: { tone: "skip", icon: "skip" },
};

export function ResultChip({ status, stale }: { status: ResultStatus | null; stale?: boolean }) {
  const { t } = useTranslation();
  if (stale) {
    return (
      <span
        title={t("status.staleHint")}
        className="inline-flex items-center gap-1.5 rounded-full bg-stale-bg px-2.5 text-xs leading-5 font-semibold whitespace-nowrap text-stale-fg"
      >
        <Icon name="stale" size={13} />
        {t("status.stale")}
      </span>
    );
  }
  if (!status) return <Chip tone="outline">{t("status.untested")}</Chip>;
  const r = RESULT[status];
  return (
    <Chip tone={r.tone} icon={r.icon}>
      {t(`status.${status}`)}
    </Chip>
  );
}

/** Small round status dot with an icon, for dense lists (runner sidebar). */
export function StatusIcon({ status, stale }: { status: ResultStatus | null; stale?: boolean }) {
  const { t } = useTranslation();
  const label = stale ? t("status.stale") : status ? t(`status.${status}`) : t("status.untested");
  if (stale) {
    return (
      <span title={label} className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-stale-bg text-stale-fg">
        <Icon name="stale" size={12} strokeWidth={2.4} />
        <span className="sr-only">{label}</span>
      </span>
    );
  }
  if (!status) {
    return (
      <span title={label} className="inline-flex size-5 shrink-0 rounded-full border-[1.5px] border-dashed border-untested">
        <span className="sr-only">{label}</span>
      </span>
    );
  }
  const r = RESULT[status];
  const bg = { pass: "bg-pass-bg text-pass-fg", fail: "bg-fail-bg text-fail-fg", blocked: "bg-blocked-bg text-blocked-fg", skip: "bg-skip-bg text-skip-fg" }[
    r.tone as "pass" | "fail" | "blocked" | "skip"
  ];
  return (
    <span title={label} className={cx("inline-flex size-5 shrink-0 items-center justify-center rounded-full", bg)}>
      <Icon name={r.icon} size={12} strokeWidth={2.6} />
      <span className="sr-only">{label}</span>
    </span>
  );
}

const STAGE: Record<TestStage, { tone: Tone; icon?: IconName }> = {
  not_started: { tone: "neutral" },
  ready_for_test: { tone: "accent", icon: "flag" },
  in_testing: { tone: "accent", icon: "play" },
  changes_requested: { tone: "fail", icon: "x" },
  ready_for_retest: { tone: "accent", icon: "stale" },
  passed: { tone: "pass", icon: "check" },
};

/**
 * Where testing stands (derived from rounds), not the task's status. With `round` (the latest), it adds the round
 * being tested ("In testing · round 2") or what the last one found ("Changes requested · 3 failed").
 */
export function TestStageChip({ stage, hideNotStarted, round }: { stage: TestStage; hideNotStarted?: boolean; round?: RoundSummary | null }) {
  const { t } = useTranslation();
  if (hideNotStarted && stage === "not_started") return null;
  const s = STAGE[stage];
  const detail =
    !round ? null
    : stage === "in_testing" ? t("testStageDetail.round", { n: round.number })
    : stage === "changes_requested" ? t("testStageDetail.failed", { count: round.counts.fail + round.counts.blocked })
    : stage === "ready_for_retest" ? t("testStageDetail.round", { n: round.number + 1 })
    : null;
  return (
    <Chip tone={s.tone} icon={s.icon}>
      {t(`testStage.${stage}`)}
      {detail && <span className="font-normal opacity-80"> · {detail}</span>}
    </Chip>
  );
}

/** The colour of a status category: grey to do, blue in progress, green done, dashed closed. */
export const CATEGORY_DOT: Record<StatusCategory, string> = {
  not_started: "border-[1.5px] border-muted bg-transparent",
  active: "bg-accent",
  done: "bg-pass-fg",
  closed: "border-[1.5px] border-dashed border-untested bg-transparent",
};

export function CategoryDot({ category, className }: { category: StatusCategory; className?: string }) {
  return <span aria-hidden="true" className={cx("inline-block size-2.5 shrink-0 rounded-full", CATEGORY_DOT[category], className)} />;
}

/** A status's dot in its chosen colour, or its category's when it has none. */
export function StatusDot({ category, color, className }: { category: StatusCategory; color?: StatusColor | null; className?: string }) {
  return <span aria-hidden="true" className={cx("inline-block size-2.5 shrink-0 rounded-full", columnTone({ category, color }).dot, className)} />;
}

/** A task's status: the project's own word, coloured by its category. */
export function StatusPill({ status, className }: { status: TaskStatusRef; className?: string }) {
  const tone = {
    not_started: "bg-surface-2 text-text-2",
    active: "bg-accent-soft text-accent",
    done: "bg-pass-bg text-pass-fg",
    closed: "bg-surface-2 text-muted line-through decoration-1",
  }[status.category];
  return (
    <span
      className={cx(
        "inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold leading-5",
        tone,
        className,
      )}
    >
      <CategoryDot category={status.category} className="size-2" />
      <span className="truncate">{status.name}</span>
    </span>
  );
}

export function PriorityLabel({ priority }: { priority: Priority }) {
  const { t } = useTranslation();
  const dot = { critical: "bg-[#b42318]", high: "bg-[#c4461a]", medium: "bg-[#c29200]", low: "bg-untested" }[priority];
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold whitespace-nowrap text-text-2">
      <span aria-hidden="true" className={cx("inline-block size-[7px] rounded-[2px]", dot)} />
      {t(`priority.${priority}`)}
    </span>
  );
}

export function Tag({ children }: { children: string }) {
  return <span className="inline-block rounded-[5px] bg-surface-2 px-1.5 text-xs leading-5 text-text-2">{children}</span>;
}

/** Stacked bar: pass / fail / blocked / skipped; the rest is untested. Stale results are
 * counted inside their status, so they only show in the legend. */
export function ProgressBar({ counts, className }: { counts: RoundCounts; className?: string }) {
  const { t } = useTranslation();
  const total = Math.max(counts.total, 1);
  const seg = (n: number, color: string) =>
    n > 0 ? <i key={color} style={{ width: `${(n / total) * 100}%`, background: `var(${color})` }} className="block" /> : null;
  return (
    <div
      role="img"
      aria-label={t("status.summary", counts)}
      className={cx("flex h-2 overflow-hidden rounded-full bg-surface-2", className)}
    >
      {seg(counts.pass, "--pass-fg")}
      {seg(counts.fail, "--fail-fg")}
      {seg(counts.blocked, "--blocked-fg")}
      {seg(counts.skipped, "--skip-fg")}
    </div>
  );
}

export function CountsLegend({ counts }: { counts: RoundCounts }) {
  const { t } = useTranslation();
  const items: [number, string, string][] = [
    [counts.pass, "--pass-fg", t("status.nPass", { count: counts.pass })],
    [counts.fail, "--fail-fg", t("status.nFail", { count: counts.fail })],
    [counts.blocked, "--blocked-fg", t("status.nBlocked", { count: counts.blocked })],
    [counts.skipped, "--skip-fg", t("status.nSkipped", { count: counts.skipped })],
    [counts.stale, "--stale-fg", t("status.nStale", { count: counts.stale })],
  ];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-text-2">
      {items
        .filter(([n]) => n > 0)
        .map(([, color, label]) => (
          <span key={color} className="inline-flex items-center gap-1.5">
            <i aria-hidden="true" className="inline-block size-[9px] rounded-[3px]" style={{ background: `var(${color})` }} />
            {label}
          </span>
        ))}
      {counts.untested > 0 && (
        <span className="inline-flex items-center gap-1.5">
          <i aria-hidden="true" className="inline-block size-[9px] rounded-[3px] border-[1.5px] border-dashed border-untested" />
          {t("status.nUntested", { count: counts.untested })}
        </span>
      )}
    </div>
  );
}

export function UserBadge({ user, fallback }: { user: UserRef | null; fallback?: string }) {
  if (!user) return <span className="text-muted">{fallback ?? "—"}</span>;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <Avatar name={user.display_name} bot={user.is_bot} size={22} />
      <span className="truncate">{user.display_name}</span>
      {user.is_bot && <BotTag />}
    </span>
  );
}
